// Documents (`/documents`): the company library the iPhone keeps under "Company documents" (price sheets, brochures,
// contracts), read-only like the rest of Phase 1. A port of DocumentsView.swift: one card per category, General
// first, each document with its kind icon, type, when and by whom it was added, and Open. Files open through the
// signed-URL cache every photo uses (`useFileUrl`); a document whose file never reached the server, or that storage
// refuses, says "File not available". Adding, renaming and deleting stay in the app.
//
// Every member may read the library (storage `can_read_file`: documents → any active member), reps included. The
// search lives in the URL query (replace, not push), like the Territories list.

import {
  Download,
  ExternalLink,
  File as FileIcon,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Files,
  Presentation,
  SearchX,
  type LucideIcon,
} from 'lucide-react';
import { memo, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useDocuments, useUsers, useViewer } from '@/data/hooks';
import { formatGrouped } from '@/domain/format';
import { byId } from '@/domain/util';
import { RequireFirstSync, ScreenLoading, useDocumentTitle } from '@/features/common';
import { NewerAppBadge } from '@/features/stores/NewerAppBadge';
import { useCan } from '@/session/permissions';
import { useFileUrl } from '@/sync/hooks';
import { EmptyState, IconBadge, PageContainer, PageHeader, SearchField, SectionCard, Spinner } from '@/ui';
import {
  DOCUMENT_KIND_TITLES,
  documentLibrary,
  documentMetaLine,
  type DocumentKind,
  type DocumentRowModel,
} from './documentsModel';

/** `DocumentRow.icon`: doc.richtext, photo, doc.text, tablecells, rectangle.on.rectangle, film, doc. */
const KIND_ICONS: Readonly<Record<DocumentKind, LucideIcon>> = {
  pdf: FileText,
  image: FileImage,
  text: FileText,
  sheet: FileSpreadsheet,
  slides: Presentation,
  video: FileVideo,
  other: FileIcon,
};

const plural = (n: number, one: string, many: string) => `${formatGrouped(n)} ${n === 1 ? one : many}`;

export default function DocumentsPage() {
  useDocumentTitle('Documents');
  return (
    <PageContainer width="default">
      <RequireFirstSync what="Documents" fallback={<ScreenLoading layout="list" count={4} label="Loading documents…" />}>
        <DocumentList />
      </RequireFirstSync>
    </PageContainer>
  );
}

function DocumentList() {
  const can = useCan();
  const viewer = useViewer();
  const documents = useDocuments();
  const users = useUsers();

  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const setQuery = (value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete('q');
    else next.set('q', value);
    setParams(next, { replace: true });
  };

  const usersById = useMemo(() => byId(users.values), [users]);
  // Every live item, newer-app ones included (badged): the library should not hide a file someone added.
  const library = useMemo(() => documentLibrary(documents.items, { users: usersById, query }), [documents, usersById, query]);

  return (
    <>
      <PageHeader
        title="Documents"
        subtitle={
          library.total > 0
            ? `${plural(library.total, 'document', 'documents')} · ${plural(library.categories, 'category', 'categories')}`
            : undefined
        }
      >
        {library.total > 0 && (
          <SearchField
            value={query}
            onValueChange={setQuery}
            label="Search documents"
            size="sm"
            slashShortcut
            className="w-full max-w-sm"
          />
        )}
      </PageHeader>

      {library.total === 0 ? (
        <EmptyState
          icon={<Files />}
          color="#29A85C"
          title="No documents yet"
          message={
            can.isManager
              ? 'Price sheets, brochures and contracts added in the Ground Team app show up here for the whole team.'
              : "Your company hasn't shared any documents yet."
          }
        />
      ) : library.matched === 0 ? (
        <EmptyState icon={<SearchX />} title="No matches" message="Try a different document name or category." />
      ) : (
        <div className="flex flex-col gap-5 pb-8">
          {library.groups.map((g) => (
            <SectionCard key={g.category} title={`${g.category} (${formatGrouped(g.rows.length)})`} padding="none">
              <ul aria-label={g.category} className="divide-y divide-line px-4">
                {g.rows.map((row) => (
                  <DocumentItem key={row.id} row={row} viewerId={viewer?.profileId} />
                ))}
              </ul>
            </SectionCard>
          ))}
        </div>
      )}
    </>
  );
}

const DocumentItem = memo(function DocumentItem({ row, viewerId }: { row: DocumentRowModel; viewerId?: string | null }) {
  const file = useFileUrl(row.filePath ?? null);
  const Icon = KIND_ICONS[row.kind];
  const missing = !row.filePath || file.status === 'missing';
  const action = row.previewable ? 'Open' : 'Download';
  return (
    <li className="flex items-center gap-3 py-3">
      <IconBadge icon={<Icon />} color={row.color} size={36} />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-fg" title={row.fileName || undefined}>
            {row.name}
          </span>
          {row.newer && <NewerAppBadge />}
        </span>
        <span className="block truncate text-xs text-fg-muted">
          <span className="sr-only">{DOCUMENT_KIND_TITLES[row.kind]}, </span>
          {documentMetaLine(row, { viewerId })}
        </span>
      </span>
      {missing ? (
        <span className="shrink-0 text-xs text-fg-subtle">File not available</span>
      ) : file.status === 'ready' ? (
        <a
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-link hover:underline"
        >
          {action}
          {row.previewable ? <ExternalLink aria-hidden className="size-3.5" /> : <Download aria-hidden className="size-3.5" />}
          <span className="sr-only">
            {' '}
            {row.name} ({row.previewable ? 'opens in a new tab' : 'downloads the file'})
          </span>
        </a>
      ) : (
        <Spinner size="sm" label={`Preparing ${row.name}`} />
      )}
    </li>
  );
});
